import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useCurrency } from "../contexts/CurrencyContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Store,
  Save,
  Upload,
  Phone,
  MapPin,
  Mail,
  FileText,
  Loader2,
  Check,
  Image as ImageIcon,
  DollarSign
} from "lucide-react";
import axios from "axios";
import toast from "react-hot-toast";

const API = window.location.origin;

export default function StoreSettingsPage() {
  const { token } = useAuth();
  const { refresh: refreshCurrency } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  
  const [storeData, setStoreData] = useState({
    store_name: "",
    store_name_secondary: "",
    address: "",
    city: "",
    phone1: "",
    phone2: "",
    email: "",
    logo_url: "",
    footer_text: "",
    tax_number: "",
    commercial_register: "",
    usd_exchange_rate: 1370,
    show_usd: true
  });

  useEffect(() => {
    fetchStoreSettings();
  }, []);

  const fetchStoreSettings = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/api/store-settings`, { headers });
      if (res.data) {
        setStoreData(res.data);
        if (res.data.logo_url) {
          setLogoPreview(res.data.logo_url);
        }
      }
    } catch (error) {
      console.log("No store settings found, using defaults");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setStoreData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("حجم الصورة يجب أن يكون أقل من 2 ميجابايت");
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setStoreData(prev => ({ ...prev, logo_url: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const saveSettings = async () => {
    if (!storeData.store_name) {
      toast.error("يرجى إدخال اسم المحل");
      return;
    }

    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/api/store-settings`, storeData, { headers });
      toast.success("تم حفظ إعدادات المحل بنجاح");
      // refresh global currency context so all pages pick up new rate immediately
      refreshCurrency();
    } catch (error) {
      toast.error("حدث خطأ في حفظ الإعدادات");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Store className="w-7 h-7 text-emerald-500" />
            إعدادات المحل
          </h1>
          <p className="text-slate-400 mt-1">
            أضف معلومات محلك لتظهر في الفواتير وكشوفات الحساب
          </p>
        </div>
        <Button
          onClick={saveSettings}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 ml-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 ml-2" />
          )}
          حفظ الإعدادات
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* معلومات المحل الأساسية */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-500" />
              معلومات المحل
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-slate-300">اسم المحل (الرئيسي) *</Label>
              <Input
                value={storeData.store_name}
                onChange={(e) => handleInputChange("store_name", e.target.value)}
                placeholder="مثال: سكلة الاعمار والبناء"
                className="bg-slate-900 border-slate-700 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-slate-300">اسم المحل (الثانوي)</Label>
              <Input
                value={storeData.store_name_secondary}
                onChange={(e) => handleInputChange("store_name_secondary", e.target.value)}
                placeholder="مثال: لتجارة الحديد والاسمنت"
                className="bg-slate-900 border-slate-700 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-slate-300">المدينة</Label>
              <Input
                value={storeData.city}
                onChange={(e) => handleInputChange("city", e.target.value)}
                placeholder="مثال: بغداد"
                className="bg-slate-900 border-slate-700 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-slate-300">العنوان التفصيلي</Label>
              <Textarea
                value={storeData.address}
                onChange={(e) => handleInputChange("address", e.target.value)}
                placeholder="مثال: القائم - الكرابلة - الشارع العام - قرب مطحنة السلامة"
                className="bg-slate-900 border-slate-700 text-white mt-1"
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* شعار المحل */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-emerald-500" />
              شعار المحل
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-600 rounded-xl">
              {logoPreview ? (
                <div className="text-center">
                  <img
                    src={logoPreview}
                    alt="شعار المحل"
                    className="w-32 h-32 object-contain mx-auto mb-4 rounded-lg bg-white p-2"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setLogoPreview(null);
                      setStoreData(prev => ({ ...prev, logo_url: "" }));
                    }}
                    className="text-red-400 border-red-400 hover:bg-red-400/10"
                  >
                    إزالة الشعار
                  </Button>
                </div>
              ) : (
                <label className="cursor-pointer text-center">
                  <Upload className="w-12 h-12 text-slate-500 mx-auto mb-3" />
                  <p className="text-slate-400 mb-2">اضغط لرفع شعار المحل</p>
                  <p className="text-slate-500 text-sm">PNG, JPG (أقصى حجم: 2 ميجابايت)</p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </CardContent>
        </Card>

        {/* معلومات التواصل */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Phone className="w-5 h-5 text-emerald-500" />
              معلومات التواصل
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-slate-300">رقم الهاتف الأول</Label>
              <Input
                value={storeData.phone1}
                onChange={(e) => handleInputChange("phone1", e.target.value)}
                placeholder="07xxxxxxxxx"
                className="bg-slate-900 border-slate-700 text-white mt-1"
                dir="ltr"
              />
            </div>

            <div>
              <Label className="text-slate-300">رقم الهاتف الثاني</Label>
              <Input
                value={storeData.phone2}
                onChange={(e) => handleInputChange("phone2", e.target.value)}
                placeholder="07xxxxxxxxx"
                className="bg-slate-900 border-slate-700 text-white mt-1"
                dir="ltr"
              />
            </div>

            <div>
              <Label className="text-slate-300">البريد الإلكتروني</Label>
              <Input
                value={storeData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder="example@email.com"
                className="bg-slate-900 border-slate-700 text-white mt-1"
                dir="ltr"
              />
            </div>
          </CardContent>
        </Card>

        {/* معلومات إضافية */}
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-500" />
              معلومات إضافية
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-slate-300">الرقم الضريبي</Label>
              <Input
                value={storeData.tax_number}
                onChange={(e) => handleInputChange("tax_number", e.target.value)}
                placeholder="الرقم الضريبي (اختياري)"
                className="bg-slate-900 border-slate-700 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-slate-300">رقم السجل التجاري</Label>
              <Input
                value={storeData.commercial_register}
                onChange={(e) => handleInputChange("commercial_register", e.target.value)}
                placeholder="رقم السجل التجاري (اختياري)"
                className="bg-slate-900 border-slate-700 text-white mt-1"
              />
            </div>

            <div>
              <Label className="text-slate-300">نص تذييل الفاتورة</Label>
              <Textarea
                value={storeData.footer_text}
                onChange={(e) => handleInputChange("footer_text", e.target.value)}
                placeholder="مثال: الأخطاء والسهو مرجوع للطرفين"
                className="bg-slate-900 border-slate-700 text-white mt-1"
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* إعدادات العملة (الدولار) */}
        <Card className="bg-slate-800 border-slate-700 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-500" />
              إعدادات العملة (الدولار)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-900 rounded-lg border border-slate-700">
              <div>
                <Label className="text-white font-bold">عرض ما يقابل المبلغ بالدولار</Label>
                <p className="text-slate-400 text-sm mt-1">
                  بجنب كل سعر/مجموع بالدينار، يظهر ما يقابله بالدولار تلقائياً
                </p>
              </div>
              <Switch
                checked={storeData.show_usd}
                onCheckedChange={(value) => handleInputChange("show_usd", value)}
                data-testid="toggle-show-usd"
              />
            </div>

            <div>
              <Label className="text-slate-300">سعر صرف الدولار (دينار / دولار)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number"
                  min="100"
                  step="1"
                  value={storeData.usd_exchange_rate}
                  onChange={(e) => handleInputChange("usd_exchange_rate", parseFloat(e.target.value) || 0)}
                  placeholder="1370"
                  className="bg-slate-900 border-slate-700 text-white"
                  dir="ltr"
                  data-testid="input-usd-rate"
                />
                <span className="text-slate-400 text-sm whitespace-nowrap">د.ع لكل $1</span>
              </div>
              <p className="text-xs text-emerald-400/80 mt-2">
                مثال: 1,000,000 د.ع ÷ {storeData.usd_exchange_rate || 1370} = ${storeData.usd_exchange_rate ? (1000000 / storeData.usd_exchange_rate).toLocaleString('en-US', {maximumFractionDigits: 2}) : "—"}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                💡 السعر قابل للتعديل بأي وقت. الفواتير القديمة لن تتأثر، فقط طريقة العرض.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* معاينة */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">معاينة رأس كشف الحساب</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-white rounded-lg p-6 text-black">
            <div className="flex items-start justify-between border-b-2 border-amber-500 pb-4">
              <div className="flex items-center gap-4">
                {logoPreview ? (
                  <img src={logoPreview} alt="شعار" className="w-20 h-20 object-contain" />
                ) : (
                  <div className="w-20 h-20 bg-gray-200 rounded flex items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                <div>
                  <h2 className="text-2xl font-bold text-amber-600">
                    {storeData.store_name || "اسم المحل"}
                  </h2>
                  {storeData.store_name_secondary && (
                    <p className="text-amber-500 text-lg">{storeData.store_name_secondary}</p>
                  )}
                </div>
              </div>
              <div className="text-left text-sm">
                <p className="font-bold text-lg text-amber-600">كشف حساب</p>
              </div>
            </div>
            
            <div className="mt-4 flex justify-between text-sm">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-amber-500" />
                <span>{storeData.city} {storeData.address ? `- ${storeData.address}` : ""}</span>
              </div>
              <div className="flex items-center gap-4">
                {storeData.phone1 && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-4 h-4 text-amber-500" />
                    {storeData.phone1}
                  </span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
